import { useEngagementScopeForm } from "PFApp/activities/activity_form/hooks/use_engagement_scope_form";
import { useParentActivityRolesIds } from "PFApp/activities/activity_form/hooks/use_parent_activity_roles_ids";
import { LS_KEY_ENGAGEMENT_SCOPE_NEW_ACTIVITY_URL } from "PFApp/constants/local_storage_keys";
import { TemplateKey } from "PFApp/constants/templates";
import { ArrowsNavigation } from "PFComponents/arrows_navigation";
import { Button } from "PFComponents/button";
import ButtonGroup from "PFComponents/button_group/button_group";
import { Checkbox } from "PFComponents/checkbox";
import { Inline } from "PFComponents/containers";
import { DropdownOption } from "PFComponents/dropdown";
import { DropdownButton } from "PFComponents/dropdown_button";
import { Activity } from "PFCore/types";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";

import { useActivityFormStore } from "../../../activity_form_store";
import { useActivityFormSubmit } from "../../../hooks/use_activity_form_submit";
import css from "../footer.module.scss";
import { EditModeButtons } from "./parts/edit_mode_buttons";

const CREATE_AND_CLONE_ID = "create_and_clone";

export const EngagementScopeFooter = () => {
  const { t } = useTranslation("activities", { keyPrefix: "form.footer" });
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();

  const { isNew, isDraft, isAudit, isEdit } = useActivityFormStore((state) => state.activityStatus);
  const [createAsDraft, setCreateAsDraft] = useState(isDraft);

  const { parentActivityId } = useEngagementScopeForm();
  const onSubmit = useActivityFormSubmit();

  const { data: existingRolesIds = [] } = useParentActivityRolesIds({
    parentActivityId: Number(parentActivityId)
  });

  const totalCount = existingRolesIds.length + 1;
  const currentRoleIndex = isNew
    ? existingRolesIds.length
    : existingRolesIds.findIndex((existingRoleId) => existingRoleId === Number(id));

  // eslint-disable-next-line max-len
  const defaultNewRoleUrl = `/activities/${isAudit ? TemplateKey.AuditRole : TemplateKey.Role}/new?parentActivityId=${parentActivityId}`;

  const handleCreateAndAddNew = () => {
    onSubmit({
      draft: createAsDraft,
      navigateCallback: () => {
        localStorage.removeItem(LS_KEY_ENGAGEMENT_SCOPE_NEW_ACTIVITY_URL);
        navigate(defaultNewRoleUrl);
      }
    });
  };

  const handleDropdownOptionClick = (optionId: string) => {
    if (optionId === CREATE_AND_CLONE_ID) {
      onSubmit({
        draft: createAsDraft,
        navigateCallback: ({ id }: Activity) => {
          // eslint-disable-next-line max-len
          const url = `/activities/${isAudit ? TemplateKey.AuditRole : TemplateKey.Role}/new/${id}?parentActivityId=${parentActivityId}`;
          localStorage.setItem(LS_KEY_ENGAGEMENT_SCOPE_NEW_ACTIVITY_URL, url);
          navigate(url);
        }
      });
    }
  };
  const goToOtherRole = (offset: 1 | -1) => {
    const newRoleIndex = currentRoleIndex + offset;
    if (newRoleIndex === existingRolesIds.length) {
      const url = localStorage.getItem(LS_KEY_ENGAGEMENT_SCOPE_NEW_ACTIVITY_URL) || defaultNewRoleUrl;

      navigate(url);
      return;
    }
    const roleId = existingRolesIds[newRoleIndex];
    navigate(`/activities/${roleId}/edit?parentActivityId=${parentActivityId}`);
  };

  const options: DropdownOption[] = useMemo(
    () => [
      {
        id: CREATE_AND_CLONE_ID,
        displayElement: t("createAndClone"),
        item: CREATE_AND_CLONE_ID
      }
    ],
    [t]
  );

  return (
    <>
      {totalCount > 1 && (
        <ArrowsNavigation
          totalCount={totalCount}
          currentIndex={currentRoleIndex}
          label={t(isAudit ? "auditRoleCounter" : "roleCounter", {
            current: currentRoleIndex + 1,
            total: totalCount
          })}
          prevAriaLabel={t("previousRole")}
          nextAriaLabel={t("nextRole")}
          className={css.rolesNavigationButtons}
          onPrevClick={() => goToOtherRole(-1)}
          onNextClick={() => goToOtherRole(1)}
        />
      )}
      <Inline as="span" gap="spacingLg">
        {isNew && (
          <>
            <Checkbox label={t("createAsDraft")} checked={createAsDraft} onChange={setCreateAsDraft} />
            <ButtonGroup compact autoWidth>
              <Button
                kind="secondary"
                text={t("createAndAddNew")}
                onClick={() => handleCreateAndAddNew()}
                className={css.footerButton}
              />
              <DropdownButton
                qaId="EngagementWizard.CreateDropdown"
                buttonKind="secondary"
                className={css.arrowDropdownButton}
                icon="chevron-down"
                options={options}
                handleChange={handleDropdownOptionClick}
                floatingOptions={{ placement: "bottom-end" }}
              />
            </ButtonGroup>
            <Button
              kind="primary"
              text={t("createAndDone")}
              onClick={() => onSubmit({ draft: createAsDraft })}
              className={css.footerButton}
            />
          </>
        )}
        {isEdit && <EditModeButtons submitOptions={{ navigateCallback: () => goToOtherRole(1) }} />}
      </Inline>
    </>
  );
};
